import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import "../styles/products.css";
import logo from "../assets/logo1.png";
import exit from "../assets/exit.png";
import SelectOption from "./MUI/SelectOption";
import { useNavigate, useParams } from "react-router-dom";
import filter from "../assets/filter.png";
import { getAllProducts, getAllTags, getAllCategories } from "../Utils/Utils";
import { useCart } from "../Context/CartContext";
import AlertOverlay from "./MUI/AlertOverlay";
import loader from "../assets/load.gif";

function Products() {
  // State for filters
  const [selectedBrand, setSelectedBrand] = useState("Toutes les marques");
  const [selectedTags, setSelectedTags] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  
  // Sorting state
  const [sortBy, setSortBy] = useState("default"); // default, price_asc, price_desc, newest, oldest

  // Data state
  const [allTags, setAllTags] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allWeight, setAllWeight] = useState([
    "90",
    "100",
    "110",
    "120",
    "130",
    "140",
    "150",
    "250",
    "20000",
  ]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [alertState, setAlertState] = useState({
    open: false,
    title: "",
    message: "",
  });

  // Category state
  const [selectedCategory, setSelectedCategory] = useState(
    "Toutes les catégories"
  );
  const [selectedSubCategory, setSelectedSubCategory] = useState(
    "Toutes les sous-catégories"
  );
  const [selectedWeight, setSelectedWeight] = useState("Tous les poids");
  const [categoriesData, setCategoriesData] = useState([]);
  const [availableSubcategories, setAvailableSubcategories] = useState([
    { name: "Toutes les sous-catégories", id: "all" },
  ]);

  const [toggleFilterSideOverlay, setToggleFilterSideOverlay] = useState(false);
  const navigate = useNavigate();
  const { addItem } = useCart();

  // Toggle tag selection
  const toggleTagSelection = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  // Sort products function
  const sortProducts = (products) => {
    const sorted = [...products];
    
    switch(sortBy) {
      case "price_asc":
        return sorted.sort((a, b) => Number(a.price) - Number(b.price));
      case "price_desc":
        return sorted.sort((a, b) => Number(b.price) - Number(a.price));
      case "newest":
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case "oldest":
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      default:
        return sorted;
    }
  };

  // Filter products based on selected filters
  const applyFilters = () => {
    let filtered = [...allProducts];

    // Filter by brand
    if (selectedBrand && selectedBrand !== "Toutes les marques") {
      filtered = filtered.filter(
        (product) => product.brandName === selectedBrand
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      const normalizeTag = (tag) =>
        tag == null ? "" : String(tag).trim().toLowerCase();

      filtered = filtered.filter((product) => {
        const productTags = Array.isArray(product?.tags)
          ? product.tags.map(normalizeTag)
          : [];
        return selectedTags.every((tag) =>
          productTags.includes(normalizeTag(tag))
        );
      });
    }

    // Filter by price range
    if (minPrice) {
      filtered = filtered.filter(
        (product) => Number(product.price) >= Number(minPrice)
      );
    }

    if (maxPrice) {
      filtered = filtered.filter(
        (product) => Number(product.price) <= Number(maxPrice)
      );
    }

    // Filter by category
    if (selectedCategory && selectedCategory !== "Toutes les catégories") {
      const selectedCategoryObj = categoriesData.find(
        (cat) => cat.categoryName === selectedCategory
      );

      if (selectedCategoryObj) {
        filtered = filtered.filter(
          (product) => product.category === selectedCategoryObj.id
        );
      }
    }

    // Filter by subcategory
    if (
      selectedSubCategory &&
      selectedSubCategory !== "Toutes les sous-catégories"
    ) {
      let selectedSubCategoryId = null;

      for (const category of categoriesData) {
        const subcat = category.subcategories.find(
          (sub) => sub.subcategoryName === selectedSubCategory
        );
        if (subcat) {
          selectedSubCategoryId = subcat.id;
          break;
        }
      }

      if (selectedSubCategoryId) {
        filtered = filtered.filter(
          (product) => product.subCategory === selectedSubCategoryId
        );
      }
    }

    // Filter by weight
    if (selectedWeight && selectedWeight !== "Tous les poids") {
      filtered = filtered.filter(
        (product) => product.weight != null && product.weight.toString() === selectedWeight.toString()
      );
    }

    // Apply sorting
    filtered = sortProducts(filtered);
    
    setFilteredProducts(filtered);
  };
  
  const { categoryId, subCategoryId } = useParams();
  console.log(categoryId);
  console.log(subCategoryId);
  
  // Fetch products and initialize data

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToSection("products-section");

    const fetchData = async () => {
      try {
        // Fetch products
        const productsRes = await getAllProducts();
        if (productsRes) {
          const productsArray = Object.values(productsRes).map((product) => ({
            ...product,
            id:
              product.id ||
              Object.keys(productsRes).find(
                (key) => productsRes[key] === product
              ),
          }));

          setAllProducts(productsArray);
          
          // Apply sorting to initial products
          const sortedProducts = sortProducts(productsArray);
          setFilteredProducts(sortedProducts);

          // Extract unique brands
          const brands = [
            "Toutes les marques",
            ...new Set(productsArray.map((p) => p.brandName).filter(Boolean)),
          ];
          setAllBrands(brands);
        }

        // Fetch categories
        const categoriesRes = await getAllCategories();
        setCategoriesData(categoriesRes);

        // Fetch tags
        const tagsRes = await getAllTags();
        if (tagsRes) {
          setAllTags(tagsRes);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // 1️⃣ Set selectedCategory when categoryId is available
  useEffect(() => {
    if (categoryId && categoriesData.length > 0) {
      const category = categoriesData.find((cat) => cat.id === categoryId);
      if (category) {
        setSelectedCategory(category.categoryName);
      }
    }
  }, [categoryId, categoriesData]);

  // 2️⃣ Set selectedSubCategory AFTER selectedCategory is set
  useEffect(() => {
    if (
      selectedCategory &&
      selectedCategory !== "Toutes les catégories" &&
      subCategoryId &&
      categoriesData.length > 0
    ) {
      const selectedCat = categoriesData.find(
        (cat) => cat.categoryName === selectedCategory
      );
      if (selectedCat) {
        const sub = selectedCat.subcategories.find(
          (sub) => sub.id === subCategoryId
        );
        if (sub) {
          console.log(
            "Subcategory matched from URL param" + sub.subcategoryName
          );
          setTimeout(() => {
            setSelectedSubCategory(sub.subcategoryName);
            console.log(selectedSubCategory);
          }, 0);
        }
      }
    }
  }, [selectedCategory, subCategoryId, categoriesData]);

  // Update available subcategories when category changes
  useEffect(() => {
    console.log("....");
    if (selectedCategory === "Toutes les catégories") {
      setAvailableSubcategories([
        { name: "Toutes les sous-catégories", id: "all" },
      ]);
      if (true) {
        setSelectedSubCategory("Toutes les sous-catégories");
      }
    } else {
      const category = categoriesData.find(
        (cat) => cat.categoryName === selectedCategory
      );
      console.log(categoriesData);
      console.log(selectedCategory);
      console.log(category);
      if (category) {
        const subcats = [
          { name: "Toutes les sous-catégories", id: "all" },
          ...category.subcategories.map((sub) => ({
            name: sub.subcategoryName,
            id: sub.id,
          })),
        ];
        console.log(subcats);
        setAvailableSubcategories(subcats);
        setSelectedSubCategory("Toutes les sous-catégories");
      }
    }
  }, [selectedCategory, categoriesData]);

  // Apply filters whenever filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [
    selectedBrand,
    selectedTags,
    minPrice,
    maxPrice,
    selectedCategory,
    selectedSubCategory,
    allProducts,
    selectedWeight,
    sortBy, // Add sortBy as dependency
  ]);

  const handleViewProduct = (product) => {
    navigate(`/product-details/${product.id}`, { state: { product } });
  };

  // Sorting options for SelectOption
  const sortOptions = [
    "Par défaut",
    "Prix croissant",
    "Prix décroissant",
    "Plus récents",
    "Plus anciens"
  ];
  
  const sortValues = {
    "Par défaut": "default",
    "Prix croissant": "price_asc",
    "Prix décroissant": "price_desc",
    "Plus récents": "newest",
    "Plus anciens": "oldest"
  };

  return (
    <>
    <motion.div
      id="products-section"
      className="section home px-80 py-40 box-sizing prodcut-container"
    >
      <div className="flex justify-space-between w-full my-40 ">
        <motion.div
          className="mobile-filters-container bold h-full  my-20 absolute"
          initial={{ right: "-100%" }}
          animate={toggleFilterSideOverlay ? { right: 0 } : { right: "-100%" }}
        >
          <img
            alt=""
            src={exit}
            className="logo-img exit-btn"
            onClick={() => {
              setToggleFilterSideOverlay(false);
            }}
          />
          
          {/* Sort Filter - Mobile */}
          <div className="filter-container">
            <p className="grey small-text">Trier par</p>
            <div className="filter-options">
              <SelectOption
                options={sortOptions}
                onOptionSelect={(option) => setSortBy(sortValues[option])}
                labelBgColor={"#6363630d"}
                selectedValue={sortOptions.find(opt => sortValues[opt] === sortBy) || "Par défaut"}
              />
            </div>
          </div>
          
          {/* Brand Filter */}
          <div className="filter-container">
            <p className="grey small-text">Marque</p>
            <div className="filter-options">
              <SelectOption
                options={allBrands}
                onOptionSelect={setSelectedBrand}
                labelBgColor={"#6363630d"}
                selectedValue={selectedBrand}
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="filter-container">
            <p className="grey small-text my-10">Catégorie</p>
            <div className="filter-options">
              <SelectOption
                options={[
                  "Toutes les catégories",
                  ...categoriesData.map((category) => category.categoryName),
                ]}
                onOptionSelect={setSelectedCategory}
                labelBgColor={"#6363630d"}
                selectedValue={selectedCategory}
              />
            </div>
          </div>

          {/* Subcategory Filter */}
          <div className="filter-container">
            <p className="grey small-text my-10">Sous-catégorie</p>
            <div className="filter-options">
              <SelectOption
                options={availableSubcategories.map((sub) => sub.name)}
                optionValues={availableSubcategories.map((sub) => sub.id)}
                onOptionSelect={setSelectedSubCategory}
                labelBgColor={"#6363630d"}
                selectedValue={selectedSubCategory}
              />
            </div>
          </div>
          
          <div className="filter-container">
            <p className="grey small-text my-10">Poids</p>
            <div className="filter-options">
              <SelectOption
                options={["Tous les poids", ...allWeight]}
                optionValues={["Tous les poids", ...allWeight]}
                onOptionSelect={setSelectedWeight}
                labelBgColor={"#6363630d"}
                selectedValue={selectedWeight}
              />
            </div>
          </div>

          {/* Tags Filter */}
          <div className="filter-container">
            <p className="grey small-text my-10">Étiquettes</p>
            <div className="filter-options grid-filter-options">
              {allTags.map((tag, index) => (
                <motion.div
                  key={index}
                  className="filter tag"
                  initial={{ backgroundColor: "#6363630d", color: "grey" }}
                  animate={{
                    backgroundColor: selectedTags.includes(tag)
                      ? "#00add8"
                      : "#6363630d",
                    color: selectedTags.includes(tag) ? "white" : "grey",
                  }}
                  onClick={() => toggleTagSelection(tag)}
                >
                  <p>{tag}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Price Filter */}
          <div className="filter-container">
            <p className="grey small-text my-10">Prix</p>
            <div className="filter-options">
              <input
                type="number"
                placeholder="Prix minimum"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
              <input
                type="number"
                placeholder="Prix maximum"
                className="my-10"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
            </div>
          </div>
        </motion.div>
        
        <div className="mobile">
          <p className="bold">Nos produits</p>
          <img
            src={filter}
            alt=""
            className="logo-img pointer filter-img"
            onClick={() => setToggleFilterSideOverlay(true)}
          />
        </div>

        <div className="w-20 relative box-sizing">
          <p className="bold uppercase">
            PRODUITS{" "}
            {typeof selectedCategory === "string" ? selectedCategory : ""}
          </p>
          <p className="bold">Filtres</p>

          <motion.div className="filters-container bold h-full top0 left0 my-20">
            {/* Sort Filter - Desktop */}
            <div className="filter-container">
              <p className="grey small-text">Trier par</p>
              <div className="filter-options">
                <SelectOption
                  options={sortOptions}
                  onOptionSelect={(option) => setSortBy(sortValues[option])}
                  labelBgColor={"#6363630d"}
                  selectedValue={sortOptions.find(opt => sortValues[opt] === sortBy) || "Par défaut"}
                />
              </div>
            </div>
            
            {/* Brand Filter */}
            <div className="filter-container">
              <p className="grey small-text">Marque</p>
              <div className="filter-options">
                <SelectOption
                  options={allBrands}
                  onOptionSelect={setSelectedBrand}
                  labelBgColor={"#6363630d"}
                  selectedValue={selectedBrand}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="filter-container">
              <p className="grey small-text my-10">Catégorie</p>
              <div className="filter-options">
                <SelectOption
                  options={[
                    "Toutes les catégories",
                    ...categoriesData.map((category) => category.categoryName),
                  ]}
                  onOptionSelect={setSelectedCategory}
                  labelBgColor={"#6363630d"}
                  selectedValue={selectedCategory}
                />
              </div>
            </div>

            {/* Subcategory Filter */}
            <div className="filter-container">
              <p className="grey small-text my-10">Sous-catégorie</p>
              <div className="filter-options">
                <SelectOption
                  options={availableSubcategories.map((sub) => sub.name)}
                  optionValues={availableSubcategories.map((sub) => sub.id)}
                  onOptionSelect={setSelectedSubCategory}
                  labelBgColor={"#6363630d"}
                  selectedValue={selectedSubCategory}
                />
              </div>
            </div>

            <div className="filter-container">
              <p className="grey small-text my-10">Poids</p>
              <div className="filter-options">
                <SelectOption
                  options={["Tous les poids", ...allWeight]}
                  optionValues={["Tous les poids", ...allWeight]}
                  onOptionSelect={setSelectedWeight}
                  labelBgColor={"#6363630d"}
                  selectedValue={selectedWeight}
                />
              </div>
            </div>

            {/* Tags Filter */}
            <div className="filter-container">
              <p className="grey small-text my-10">Étiquettes</p>
              <div className="filter-options grid-filter-options">
                {allTags.map((tag, index) => (
                  <motion.div
                    key={index}
                    className="filter tag"
                    initial={{ backgroundColor: "#6363630d", color: "grey" }}
                    animate={{
                      backgroundColor: selectedTags.includes(tag)
                        ? "#00add8"
                        : "#6363630d",
                      color: selectedTags.includes(tag) ? "white" : "grey",
                    }}
                    onClick={() => toggleTagSelection(tag)}
                  >
                    <p>{tag}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Price Filter */}
            <div className="filter-container">
              <p className="grey small-text my-10">Prix</p>
              <div className="filter-options">
                <input
                  type="number"
                  placeholder="Prix minimum"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Prix maximum"
                  className="my-10"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Products Grid */}
        <div className="w-75 relative">
          <motion.div
            className="flex justify-center align-items-center absolute products-loader"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0, transition: { delay: 2 } }}
          >
            <motion.img alt="" src={loader} className="logo-img" />
          </motion.div>
          <motion.div
            className="flex align-items-center products justify-space-between box-sizing flex-wrap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 2 } }}
          >
            {filteredProducts.map((item, index) => (
              <motion.div
                key={item.id}
                className="product-card bg-grey flex flex-column"
                initial={{ opacity: 0, top: "20px" }}
                animate={{
                  opacity: 1,
                  top: 0,
                  origin: "top",
                  transition: { delay: 0.02 * index },
                }}
              >

                {item && !item.outOfStock ? (
                  <div className="absolute  stock-banner b-red">En Rupture</div>
                ) : null}
                <div className="product-card-header flex align-items-center justify-flex-start">
                  <img src={logo} alt="logo" height="15" />
                </div>

                <motion.div
                  className="product-card-details flex flex-column align-items-center justify-center box-sizing"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <img
                    src={item.imagesUrls?.[0]}
                    alt={item.productName}
                    height="200"
                    style={{ objectFit: "contain" }}
                  />
                  <span className="small- black">{item.productName}</span>
                  <span className="small-text grey">{item.brandName}</span>
                </motion.div>

                <div className="product-card-footer flex align-items-center justify-space-between mt-4">
                  <div className="flex align-items-center">
                    <motion.div
                      className="action-button bg-orange"
                      onClick={() => handleViewProduct(item)}
                    >
                      <p className="btn">Voir le produit</p>
                    </motion.div>

                    {item.outOfStock && (
                      <motion.div
                        className="action-button bg-blue mx-10"
                        onClick={() => {
                          try {
                            addItem(item, 1);
                            setAlertState({
                              open: true,
                              title: "Succès",
                              message: "Ajouté au panier",
                            });
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                      >
                        <p className="btn">Ajouter</p>
                      </motion.div>
                    )}
                  </div>
                  <span className="bold grey">{item.price} DA</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.div>
    <AlertOverlay
      stat={alertState.open}
      title={alertState.title}
      message={alertState.message}
      onClose={() => setAlertState((prev) => ({ ...prev, open: false }))}
      autoCloseMs={2500}
    />
    </>
  );
}

export default Products;